import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { User, Lock, Info, CircleAlert } from 'lucide-react';
import { useAuth } from '../../stateManagers/services/AuthContext';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';

/* Logo assets — adjust paths if your images live elsewhere */
import logoLandscape from '../../assets/images/pitchout_landscape_logo.png';
import logoSquare from '../../assets/images/pitchout_square _logo.png';

/* ── Palette (unchanged) ─────────────────────────────── */
const ACCENT = '#c8e94a';
const ACCENT_HOVER = '#b8d940';
const BG = '#16706a';
const BG_DARK = '#0f4f4a';
const BG_MID = '#4a9e98';
const MINT = '#d6eeec';

const REMEMBER_KEY = 'pitchout-remembered-login';

/* Sample leads shown on the brand panel (decorative only) */
const PREVIEW_LEADS = [
  { name: 'Rohit Verma', source: 'Facebook ad', stage: 'New', tone: ACCENT },
  { name: 'Anjali Gupta', source: 'Website form', stage: 'Follow-up', tone: '#ffffff' },
  { name: 'Karan Mehta', source: 'Walk-in', stage: 'Converted', tone: BG_MID },
];

/** Square mark — used next to the wordmark on mobile */
const LogoMark = ({ size = 34 }: { size?: number }) => (
  <span className="po-logo-box po-logo-box-sm">
    <img
      src={logoSquare}
      alt=""
      width={size}
      height={size}
      style={{ objectFit: 'contain', display: 'block' }}
      aria-hidden="true"
    />
  </span>
);

const LoginScreen = () => {
  const { login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const toastRef = useRef<Toast>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Restore remembered login name
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setUsername(saved);
        setRemember(true);
      }
    } catch {
      /* storage unavailable — ignore */
    }
  }, []);

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isLoggingIn) return;

    if (!username.trim() || !password) {
      setError('Enter your login and password to continue.');
      return;
    }

    setError('');
    setIsLoggingIn(true);
    try {
      await login(username.trim(), password);
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, username.trim());
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {
        /* ignore */
      }
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Login or password is incorrect. Check both and try again.';
      setError(errMsg);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Login failed',
        detail: errMsg,
        life: 5000,
      });
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setIsLoggingIn(false);
    }
  };

  const checkCaps = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsOn(e.getModifierState?.('CapsLock') ?? false);
  };

  return (
    <div className="po-root">
      <Toast ref={toastRef} position="top-right" />

      {/* ── Brand panel (desktop) ───────────────────────── */}
      <aside className="po-brand" aria-hidden="true">
        <div className="po-brand-top">
          {/* Full landscape logo on white container */}
          <div className="po-logo-box po-logo-box-lg">
            <img
              src={logoLandscape}
              alt="PitchOut"
              className="po-landscape-logo"
            />
          </div>
        </div>

        <div className="po-dots">
          {[
            // original set
            ['12%', '78%', 10],
            ['26%', '90%', 6],
            ['58%', '84%', 8],
            ['8%', '46%', 6],
            ['40%', '70%', 5],
            // doubled set
            ['18%', '22%', 7],
            ['72%', '14%', 9],
            ['34%', '55%', 5],
            ['65%', '38%', 6],
            ['48%', '12%', 8],
          ].map(([t, l, sz], i) => (
            <span
              key={i}
              style={{
                top: t as string,
                left: l as string,
                width: sz as number,
                height: sz as number,
              }}
            />
          ))}
        </div>

        <div className="po-brand-body">
          <h2 className="po-brand-title">
            Every lead in one place,<br />ready for the next call.
          </h2>
          <p className="po-brand-copy">
            Track enquiries from ads, forms and walk-ins, and never miss a follow-up.
          </p>

          <ul className="po-leads">
            {PREVIEW_LEADS.map((l, i) => (
              <li key={l.name} className="po-lead" style={{ animationDelay: `${0.25 + i * 0.12}s` }}>
                <span className="po-avatar">{l.name.split(' ').map(n => n[0]).join('')}</span>
                <span className="po-lead-text">
                  <strong>{l.name}</strong>
                  <small>{l.source}</small>
                </span>
                <span className="po-stage">
                  <i style={{ background: l.tone }} />
                  {l.stage}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Waves */}
        <div className="po-waves">
          <svg className="po-wave po-wave-1" viewBox="0 0 2880 180" preserveAspectRatio="none">
            <path d="M0,80 C120,120 240,40 360,80 C480,120 600,40 720,80 C840,120 960,40 1080,80 C1200,120 1320,40 1440,80 C1560,120 1680,40 1800,80 C1920,120 2040,40 2160,80 C2280,120 2400,40 2520,80 C2640,120 2760,40 2880,80 L2880,180 L0,180 Z" fill={BG_DARK} opacity="0.8" />
          </svg>
          <svg className="po-wave po-wave-2" viewBox="0 0 2880 180" preserveAspectRatio="none">
            <path d="M0,110 C160,70 320,150 480,110 C640,70 800,150 960,110 C1120,70 1280,150 1440,110 C1600,70 1760,150 1920,110 C2080,70 2240,150 2400,110 C2560,70 2720,150 2880,110 L2880,180 L0,180 Z" fill={ACCENT} opacity="0.35" />
          </svg>
          <svg className="po-wave po-wave-line" viewBox="0 0 2880 180" preserveAspectRatio="none">
            <path d="M0,95 C120,125 240,65 360,95 C480,125 600,65 720,95 C840,125 960,65 1080,95 C1200,125 1320,65 1440,95 C1560,125 1680,65 1800,95 C1920,125 2040,65 2160,95 C2280,125 2400,65 2520,95 C2640,125 2760,65 2880,95" fill="none" stroke={ACCENT} strokeWidth="2.5" opacity="0.9" />
          </svg>
          <svg className="po-wave po-wave-3" viewBox="0 0 2880 180" preserveAspectRatio="none">
            <path d="M0,140 C180,110 360,170 540,140 C720,110 900,170 1080,140 C1260,110 1440,170 1620,140 C1800,110 1980,170 2160,140 C2340,110 2520,170 2700,140 C2760,128 2820,152 2880,140 L2880,180 L0,180 Z" fill={MINT} opacity="0.9" />
          </svg>
        </div>
      </aside>

      {/* ── Form panel ──────────────────────────────────── */}
      <main className="po-main">
        <div className="po-mobile-logo">
          <LogoMark size={30} />
          <span className="po-wordmark po-wordmark-dark">PitchOut</span>
        </div>

        <form className="po-card" onSubmit={handleLogin} noValidate>
          <h1 className="po-title">Sign in</h1>
          <p className="po-subtitle">Welcome back. Sign in to manage your leads.</p>

          {error && (
            <div className="po-error" role="alert">
              <CircleAlert size={15} />
              <span>{error}</span>
            </div>
          )}

          <div className="po-fields">
            <GlobalInputField
              id="po-login"
              label="Login"
              icon={User}
              value={username}
              onChange={e => { setUsername(e.target.value); setError(''); }}
              placeholder="Your username"
              autoComplete="username"
              disabled={isLoggingIn}
              invalid={!!error && !username}
              inputProps={{ autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false }}
            />

            <GlobalInputField
              id="po-password"
              label="Password"
              type="password"
              icon={Lock}
              inputRef={passwordRef}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              onKeyUp={checkCaps}
              onKeyDown={checkCaps}
              onBlur={() => setCapsOn(false)}
              placeholder="Your password"
              autoComplete="current-password"
              disabled={isLoggingIn}
              invalid={!!error && !password}
              labelRight={<a href="#" className="po-link">Forgot password?</a>}
            />
          </div>
          {capsOn && (
            <p className="po-hint">
              <Info size={13} /> Caps Lock is on
            </p>
          )}

          <label className="po-check">
            <input
              type="checkbox"
              checked={remember}
              onChange={e => setRemember(e.target.checked)}
              disabled={isLoggingIn}
            />
            <span>Remember my login on this device</span>
          </label>

          <GlobalButton
            type="submit"
            label={isLoggingIn ? 'Signing in…' : 'Sign in'}
            variant="success"
            size="large"
            loading={isLoggingIn}
            fullWidth
          />
        </form>

        <footer className="po-footer">
          © {new Date().getFullYear()} PitchOut. All rights reserved.
        </footer>
      </main>

      <style>{`
        .po-root {
          min-height: 100vh;
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
          background: ${BG};
          font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
          color: #fff;
        }

        /* Brand panel */
        .po-brand {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at 18% 22%, rgba(200,233,74,0.14), transparent 42%),
            radial-gradient(circle at 88% 78%, rgba(200,233,74,0.08), transparent 38%),
            ${BG_DARK};
          display: flex;
          flex-direction: column;
          padding: 32px 48px 0;
        }
        .po-brand-top, .po-mobile-logo { display: flex; align-items: center; gap: 8px; }

        /* White container behind logo so it stands out on dark teal */
        .po-logo-box {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          border-radius: 12px;
          box-shadow:
            0 4px 14px rgba(6, 47, 45, 0.22),
            0 1px 3px rgba(6, 47, 45, 0.12);
        }
        .po-logo-box-lg {
          padding: 1.5px 2.5px;
          border-radius: 10px;
        }
        .po-logo-box-sm {
          padding: 6px;
          border-radius: 8px;
        }

        /* Landscape logo inside white box (+25%) */
        .po-landscape-logo {
          height: 45px;
          width: auto;
          max-width: min(250px, 55vw);
          object-fit: contain;
          display: block;
        }

        .po-wordmark { font-size: 24px; font-weight: 800; letter-spacing: -1px; color: #fff; }

        .po-brand-body {
          position: relative; z-index: 2;
          margin: auto 0;
          max-width: 460px;
          padding-bottom: 140px;
        }
        .po-brand-title {
          font-size: clamp(28px, 3vw, 40px);
          line-height: 1.15;
          font-weight: 700;
          letter-spacing: -0.8px;
          margin: 0 0 14px;
        }
        .po-brand-title::after {
          content: ''; display: block;
          width: 48px; height: 4px; border-radius: 4px;
          background: ${ACCENT}; margin-top: 16px;
        }
        .po-brand-copy {
          font-size: 16px; line-height: 1.55;
          color: rgba(255,255,255,0.7);
          margin: 0 0 32px; max-width: 380px;
        }

        .po-dots { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
        .po-dots span { position: absolute; border-radius: 50%; background: ${ACCENT}; opacity: 0.5; }
        .po-dots span:nth-child(even) { background: transparent; border: 2px solid ${ACCENT}; opacity: 0.4; }

        .po-leads { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
        .po-lead {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 14px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.10);
          border-left: 3px solid ${ACCENT};
          border-radius: 12px;
          opacity: 0;
          animation: poSlideIn 0.5s ease forwards;
          transition: background 0.2s, border-color 0.2s;
        }
        .po-lead:hover { background: ${ACCENT}14; border-color: ${ACCENT}66; border-left-color: ${ACCENT}; }
        .po-lead:nth-child(2) { margin-left: 24px; }
        .po-lead:nth-child(3) { margin-left: 48px; }
        .po-avatar {
          width: 36px; height: 36px; flex-shrink: 0;
          border-radius: 50%;
          background: ${BG};
          box-shadow: 0 0 0 2px ${ACCENT};
          display: grid; place-items: center;
          font-size: 13px; font-weight: 700; color: ${ACCENT};
        }
        .po-lead-text { display: flex; flex-direction: column; flex: 1; min-width: 0; }
        .po-lead-text strong { font-size: 14px; font-weight: 600; }
        .po-lead-text small { font-size: 12px; color: rgba(255,255,255,0.55); }
        .po-stage {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 12px; color: rgba(255,255,255,0.8);
          padding: 4px 10px; border-radius: 999px;
          background: ${ACCENT}1f;
          border: 1px solid ${ACCENT}40;
        }
        .po-stage i { width: 7px; height: 7px; border-radius: 50%; display: block; }

        .po-waves { position: absolute; left: 0; right: 0; bottom: 0; height: 160px; overflow: hidden; z-index: 1; }
        .po-wave { position: absolute; bottom: 0; left: 0; width: 200%; height: 160px; }
        .po-wave-1 { animation: poWave 9s linear infinite; }
        .po-wave-2 { animation: poWave 6.5s linear infinite; }
        .po-wave-3 { animation: poWave 4.5s linear infinite; }
        .po-wave-line { animation: poWave 12s linear infinite; }

        /* Form panel — light, scaled to match the app UI */
        .po-main {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 40px 24px 24px;
          position: relative;
          background: #ffffff;
          color: ${BG_DARK};
        }
        .po-mobile-logo { display: none; margin-bottom: 26px; }
        .po-wordmark-dark { color: ${BG_DARK}; }

        .po-card {
          width: 100%; max-width: 350px;
          animation: poRise 0.5s ease both;
        }
        .po-title { font-size: 28px; font-weight: 700; letter-spacing: -0.5px; margin: 0 0 6px; color: ${BG_DARK}; }
        .po-title::after {
          content: ''; display: block;
          width: 34px; height: 4px; border-radius: 4px;
          background: ${ACCENT}; margin-top: 9px;
        }
        .po-subtitle { font-size: 14px; color: #5f7a78; margin: 0 0 26px; }

        .po-error {
          display: flex; gap: 9px; align-items: flex-start;
          background: #fdecec;
          border: 1px solid #f5c2c2;
          color: #a42b2b;
          font-size: 13px; line-height: 1.4;
          padding: 9px 11px; border-radius: 9px;
          margin-bottom: 16px;
        }
        .po-error svg { margin-top: 1px; flex-shrink: 0; }

        .po-fields { display: grid; gap: 16px; }
        .po-link {
          font-size: 12px; font-weight: 600; color: ${BG}; text-decoration: none;
          background-image: linear-gradient(${ACCENT}, ${ACCENT});
          background-size: 100% 3px; background-repeat: no-repeat; background-position: 0 100%;
          padding-bottom: 1px; transition: background-size 0.2s;
        }
        .po-link:hover { background-size: 100% 100%; color: ${BG_DARK}; }
        .po-link:focus-visible { outline: 2px solid ${BG}; outline-offset: 3px; border-radius: 4px; }

        .po-hint { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #9a6a00; margin: 8px 0 0; }

        .po-check {
          display: flex; align-items: center; gap: 9px;
          margin: 16px 0 22px;
          font-size: 13px; color: #3f5f5c;
          cursor: pointer; user-select: none;
        }
        .po-check input { width: 16px; height: 16px; margin: 0; accent-color: ${ACCENT}; cursor: pointer; }

        .po-footer { margin-top: 34px; font-size: 11px; color: #7d9896; }

        @keyframes poWave { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes poSlideIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: none; } }
        @keyframes poRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        
        /* Tablet & mobile: single column */
        @media (max-width: 900px) {
          .po-root { grid-template-columns: 1fr; }
          .po-brand { display: none; }
          .po-mobile-logo { display: flex; }
          .po-main { justify-content: flex-start; padding-top: 48px; }
        }
        @media (max-width: 480px) {
          .po-card { padding: 28px 20px 24px; border-radius: 16px; }
          .po-title { font-size: 25px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .po-wave, .po-wave-line, .po-lead, .po-card { animation: none !important; opacity: 1 !important; }
        }
      `}</style>
    </div>
  );
};

export default LoginScreen;